import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShotData } from '../types';
import { Target, Circle } from 'lucide-react';

interface WagonWheelProps {
  shots: ShotData[];
  highlightShotType?: string;
  className?: string;
}

export const WagonWheel: React.FC<WagonWheelProps> = ({ 
  shots = [], 
  highlightShotType,
  className = '' 
}) => {
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  
  // Log for debugging
  console.log('WagonWheel received shots:', shots.length);
  
  // Constants for field dimensions - oval shape (taller than wide)
  const fieldRadiusX = 180; // Narrower horizontally
  const fieldRadiusY = 220; // Longer vertically
  const centerX = 250;
  const centerY = 250;
  const boundaryRadiusX = 160;
  const boundaryRadiusY = 200;
  const innerCircleRadiusX = 50;
  const innerCircleRadiusY = 70;
  
  // Cricket field zones - matching standard wagon wheel layout
  // Batsman at CENTER
  // STRAIGHT shots go DOWN (towards bowler at bottom) = 270°
  // LEG SIDE is on the RIGHT = 0°
  // OFF SIDE is on the LEFT = 180°
  // BEHIND wicket is at TOP = 90°
  const fieldZones = [
    // Straight/Down (towards bowler at 270°)
    { name: 'Straight Drive', angle: 270, color: '#e5e7eb' },     // Bottom center
    { name: 'Long On', angle: 285, color: '#f3f4f6' },           // Bottom-slightly-right (leg side)
    { name: 'Long Off', angle: 255, color: '#e5e7eb' },          // Bottom-slightly-left (off side)
    
    // Leg side (RIGHT side - around 0°)
    { name: 'On Drive', angle: 300, color: '#f3f4f6' },          // Right-bottom
    { name: 'Mid Wicket', angle: 330, color: '#e5e7eb' },        // Right-diagonal
    { name: 'Square Leg', angle: 0, color: '#f3f4f6' },          // Right (perpendicular)
    { name: 'Pull', angle: 345, color: '#e5e7eb' },              // Right-slightly forward
    { name: 'Hook', angle: 15, color: '#f3f4f6' },               // Right-slightly back
    { name: 'Fine Leg', angle: 45, color: '#e5e7eb' },           // Right-back (behind)
    { name: 'Leg Glance', angle: 60, color: '#f3f4f6' },         // Right-back-behind
    
    // Off side (LEFT side - around 180°)
    { name: 'Off Drive', angle: 240, color: '#e5e7eb' },         // Left-bottom
    { name: 'Cover Drive', angle: 210, color: '#f3f4f6' },       // Left-diagonal
    { name: 'Cover', angle: 195, color: '#e5e7eb' },             // Left-forward
    { name: 'Square Drive', angle: 185, color: '#f3f4f6' },      // Left-forward
    { name: 'Point', angle: 180, color: '#e5e7eb' },             // Left (perpendicular)
    { name: 'Square Cut', angle: 165, color: '#f3f4f6' },        // Left-slightly back
    { name: 'Cut', angle: 150, color: '#e5e7eb' },               // Left-back
    { name: 'Late Cut', angle: 135, color: '#f3f4f6' },          // Left-back-behind
    { name: 'Third Man', angle: 120, color: '#e5e7eb' },         // Left-back (behind)
  ];
  
  // Convert shot data to field positions
  const shotPositions = useMemo(() => {
    return shots.map(shot => {
      // Convert horizontal angle to field angle
      // 0° (straight) should go to 270° (bottom - towards bowler)
      // Negative angles (leg side) go right (towards 180°)
      // Positive angles (off side) go left (towards 0°/360°)
      let fieldAngle = 270 - shot.horizontalAngle;
      if (fieldAngle < 0) fieldAngle += 360;
      if (fieldAngle >= 360) fieldAngle -= 360;
      
      // Determine if it's a six, four, or regular shot
      const isSix = shot.distance >= 80; // 80m+ is typically a six
      const isFour = shot.distance >= 60 && shot.distance < 80; // 60-80m is typically a four
      
      // Normalize distance for visualization
      let normalizedDistance;
      if (isSix) {
        // Sixes go 10-20% beyond the boundary
        normalizedDistance = 1.1 + (Math.random() * 0.1); // Slightly randomize for visual variety
      } else if (isFour) {
        // Fours reach exactly to the boundary
        normalizedDistance = 1.0;
      } else {
        // Regular shots scale up to 95% of boundary
        normalizedDistance = Math.min(shot.distance / 60, 0.95);
      }
      
      // Convert to cartesian coordinates with oval shape
      const angleRad = (fieldAngle * Math.PI) / 180;
      const x = centerX + normalizedDistance * boundaryRadiusX * Math.cos(angleRad);
      const y = centerY - normalizedDistance * boundaryRadiusY * Math.sin(angleRad); // Negative because SVG y increases downward
      
      return {
        ...shot,
        x,
        y,
        fieldAngle,
        normalizedDistance,
        isBoundary: isSix,
        isFour: isFour
      };
    });
  }, [shots]);
  
  // Calculate shot distribution by zone
  const zoneDistribution = useMemo(() => {
    const distribution: Record<string, number> = {};
    
    fieldZones.forEach(zone => {
      distribution[zone.name] = 0;
    });
    
    shotPositions.forEach(shot => {
      // Find which zone the shot belongs to based on angle
      // Zones are centered at: 270, 315, 0, 45, 90, 135, 180, 225
      let minDiff = 360;
      let closestZone = fieldZones[0];
      
      fieldZones.forEach(zone => {
        let diff = Math.abs(shot.fieldAngle - zone.angle);
        if (diff > 180) diff = 360 - diff; // Handle wrap-around
        if (diff < minDiff) {
          minDiff = diff;
          closestZone = zone;
        }
      });
      
      distribution[closestZone.name]++;
    });
    
    return distribution;
  }, [shotPositions]);
  
  // Shot quality colors
  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent': return '#10b981';
      case 'good': return '#3b82f6';
      case 'fair': return '#f59e0b';
      case 'poor': return '#ef4444';
      default: return '#6b7280';
    }
  };
  
  // Calculate statistics
  const stats = useMemo(() => {
    const totalShots = shots.length;
    const boundaries = shotPositions.filter(s => s.isBoundary).length;
    const fours = shotPositions.filter(s => s.isFour).length;
    const averageDistance = shots.reduce((sum, s) => sum + s.distance, 0) / totalShots || 0;
    
    return {
      totalShots,
      boundaries,
      fours,
      averageDistance
    };
  }, [shots, shotPositions]);
  
  return (
    <div className={`space-y-4 ${className}`}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Wagon Wheel - Shot Distribution
          </CardTitle>
          <CardDescription>
            Field placement and shot direction analysis
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row gap-6">
            {/* SVG Field Visualization */}
            <div className="flex-1">
              <svg width="500" height="500" viewBox="0 0 500 500" className="w-full h-auto">
                {/* Field sectors for oval shape */}
                {fieldZones.map((zone, _index) => {
                  // Calculate angles for this zone (45° segments)
                  const startAngle = (zone.angle - 22.5) * Math.PI / 180;
                  const endAngle = (zone.angle + 22.5) * Math.PI / 180;
                  const largeArcFlag = 0;
                  
                  // Calculate points on the ellipse
                  const x1 = centerX + fieldRadiusX * Math.cos(startAngle);
                  const y1 = centerY - fieldRadiusY * Math.sin(startAngle);
                  const x2 = centerX + fieldRadiusX * Math.cos(endAngle);
                  const y2 = centerY - fieldRadiusY * Math.sin(endAngle);
                  
                  const pathData = [
                    `M ${centerX} ${centerY}`,
                    `L ${x1} ${y1}`,
                    `A ${fieldRadiusX} ${fieldRadiusY} 0 ${largeArcFlag} 0 ${x2} ${y2}`,
                    'Z'
                  ].join(' ');
                  
                  return (
                    <g key={zone.name}>
                      <path
                        d={pathData}
                        fill={zone.color}
                        stroke="#d1d5db"
                        strokeWidth="1"
                        opacity={selectedZone === zone.name ? 1 : 0.3}
                        className="cursor-pointer transition-opacity"
                        onClick={() => setSelectedZone(selectedZone === zone.name ? null : zone.name)}
                      />
                      <text
                        x={centerX + (fieldRadiusX - 30) * Math.cos((zone.angle * Math.PI) / 180)}
                        y={centerY - (fieldRadiusY - 40) * Math.sin((zone.angle * Math.PI) / 180)}
                        textAnchor="middle"
                        className="text-xs fill-gray-600 pointer-events-none"
                      >
                        {zone.name}
                      </text>
                    </g>
                  );
                })}
                
                {/* Boundary ellipse */}
                <ellipse
                  cx={centerX}
                  cy={centerY}
                  rx={boundaryRadiusX}
                  ry={boundaryRadiusY}
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />
                
                {/* Inner ellipse (30-yard circle) */}
                <ellipse
                  cx={centerX}
                  cy={centerY}
                  rx={innerCircleRadiusX}
                  ry={innerCircleRadiusY}
                  fill="none"
                  stroke="#d1d5db"
                  strokeWidth="1"
                  strokeDasharray="3,3"
                />
                
                {/* Pitch rectangle - vertical orientation */}
                <rect
                  x={centerX - 5}
                  y={centerY - 40}
                  width="10"
                  height="120"
                  fill="#d4a574"
                  opacity="0.3"
                />
                
                {/* Batsman position (at CENTER) with crease */}
                <rect
                  x={centerX - 15}
                  y={centerY - 2}
                  width="30"
                  height="4"
                  fill="#8b4513"
                  opacity="0.4"
                />
                <circle
                  cx={centerX}
                  cy={centerY}
                  r="12"
                  fill="#1f2937"
                />
                <text
                  x={centerX}
                  y={centerY + 5}
                  textAnchor="middle"
                  className="text-[10px] fill-white font-bold"
                >
                  BAT
                </text>
                
                {/* Bowler/Straight boundary indicator (at BOTTOM) */}
                <circle
                  cx={centerX}
                  cy={centerY + 75}
                  r="6"
                  fill="#6b7280"
                  opacity="0.5"
                />
                <text
                  x={centerX}
                  y={centerY + 95}
                  textAnchor="middle"
                  className="text-xs fill-gray-600 font-semibold"
                >
                  BOWLER / STRAIGHT
                </text>
                
                {/* Wicket keeper position (at TOP - behind) */}
                <circle
                  cx={centerX}
                  cy={centerY - 40}
                  r="5"
                  fill="#94a3b8"
                  opacity="0.5"
                />
                <text
                  x={centerX}
                  y={centerY - 55}
                  textAnchor="middle"
                  className="text-xs fill-gray-600"
                >
                  WICKET KEEPER
                </text>
                
                {/* Side labels */}
                <text
                  x={centerX + 120}
                  y={centerY}
                  textAnchor="middle"
                  className="text-sm fill-gray-600 font-bold"
                  transform={`rotate(-90 ${centerX + 120} ${centerY})`}
                >
                  LEG SIDE / ON SIDE
                </text>
                <text
                  x={centerX - 120}
                  y={centerY}
                  textAnchor="middle"
                  className="text-sm fill-gray-600 font-bold"
                  transform={`rotate(90 ${centerX - 120} ${centerY})`}
                >
                  OFF SIDE
                </text>
                
                {/* Plot shots */}
                {shotPositions.map((shot, index) => {
                  const shouldHighlight = !highlightShotType || shot.shotType === highlightShotType;
                  const opacity = shouldHighlight ? 1 : 0.2;
                  
                  return (
                    <g key={index}>
                      {/* Shot line */}
                      <line
                        x1={centerX}
                        y1={centerY}
                        x2={shot.x}
                        y2={shot.y}
                        stroke={getQualityColor(shot.quality)}
                        strokeWidth={shot.isBoundary ? "3" : shot.isFour ? "2.5" : "2"}
                        strokeDasharray={shot.isBoundary ? "5,3" : "none"}
                        opacity={opacity * (shot.isBoundary ? 0.6 : 0.3)}
                      />
                      
                      {/* Shot point */}
                      <circle
                        cx={shot.x}
                        cy={shot.y}
                        r={shot.isBoundary ? 7 : shot.isFour ? 6 : 4}
                        fill={getQualityColor(shot.quality)}
                        stroke={shot.isBoundary ? "#fbbf24" : "white"}
                        strokeWidth={shot.isBoundary ? "2" : "1"}
                        opacity={opacity}
                        className="cursor-pointer"
                      >
                        <title>
                          {shot.shotType}: {shot.distance.toFixed(1)}m ({shot.quality})
                        </title>
                      </circle>
                      
                      {/* Boundary indicator */}
                      {shot.isBoundary && (
                        <text
                          x={shot.x}
                          y={shot.y}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-xs font-bold fill-white pointer-events-none"
                        >
                          6
                        </text>
                      )}
                      {shot.isFour && (
                        <text
                          x={shot.x}
                          y={shot.y}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-xs font-bold fill-white pointer-events-none"
                        >
                          4
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>
            
            {/* Statistics and Legend */}
            <div className="w-full lg:w-64 space-y-4">
              {/* Statistics */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Total Shots</p>
                  <p className="text-2xl font-bold">{stats.totalShots}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Avg Distance</p>
                  <p className="text-2xl font-bold">{stats.averageDistance.toFixed(1)}m</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Sixes</p>
                  <p className="text-2xl font-bold">{stats.boundaries}</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Fours</p>
                  <p className="text-2xl font-bold">{stats.fours}</p>
                </div>
              </div>
              
              {/* Zone Distribution */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Shot Distribution</h4>
                <div className="space-y-1">
                  {Object.entries(zoneDistribution)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5)
                    .map(([zone, count]) => (
                      <div
                        key={zone}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100"
                        onClick={() => setSelectedZone(selectedZone === zone ? null : zone)}
                      >
                        <span className="text-sm">{zone}</span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    ))}
                </div>
              </div>
              
              {/* Quality Legend */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Shot Quality</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2">
                    <Circle className="h-3 w-3 fill-green-500 text-green-500" />
                    <span className="text-xs">Excellent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Circle className="h-3 w-3 fill-blue-500 text-blue-500" />
                    <span className="text-xs">Good</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Circle className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                    <span className="text-xs">Fair</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Circle className="h-3 w-3 fill-red-500 text-red-500" />
                    <span className="text-xs">Poor</span>
                  </div>
                </div>
              </div>
              
              {/* Distance Legend */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Distance Indicators</h4>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-purple-600">6</Badge>
                    <span className="text-xs">Six (80m+)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-600">4</Badge>
                    <span className="text-xs">Four (60-80m)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Circle className="h-3 w-3" />
                    <span className="text-xs">Run (&lt;60m)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WagonWheel;